import type { FormikProps } from 'formik';
import type { TFunction } from 'i18next';

import type { AddressValidationResponse, AddressValidationSuggestion } from '../api/addressValidation';
import type { ClientFormValues } from '../models/Forms';

const SLOVAKIA_VARIANTS = new Set(['sk', 'slovakia', 'slovenska republika', 'slovensko']);
const CZECHIA_VARIANTS = new Set(['ceska republika', 'cesko', 'cz', 'czechia', 'czech republic']);

export type AddressValidationPersistStatus = NonNullable<ClientFormValues['addressValidationStatus']>;

export const addressValidationFieldNames = ['psc', 'city', 'state'] as const;

export function clearAddressValidationFieldErrors(formik: Pick<FormikProps<ClientFormValues>, 'setFieldError'>) {
  for (const field of addressValidationFieldNames) {
    formik.setFieldError(field, undefined);
  }
}

export function normalizePostalCode(value: string) {
  return value.replaceAll(/\s+/g, '');
}

function normalizeCompareText(value: string) {
  return value.normalize('NFD').replaceAll(/\p{M}/gu, '').toLowerCase().replaceAll(/\s+/g, ' ').trim();
}

export function isStateEquivalent(suggested: string | undefined, original: string | undefined): boolean {
  const normalizedSuggested = normalizeCompareText(suggested ?? '');
  const normalizedOriginal = normalizeCompareText(original ?? '');
  if (normalizedSuggested === normalizedOriginal) {
    return true;
  }
  if (SLOVAKIA_VARIANTS.has(normalizedSuggested) && SLOVAKIA_VARIANTS.has(normalizedOriginal)) {
    return true;
  }
  if (CZECHIA_VARIANTS.has(normalizedSuggested) && CZECHIA_VARIANTS.has(normalizedOriginal)) {
    return true;
  }
  return false;
}

export function hasActionableAddressSuggestionDiff(
  suggestion: AddressValidationSuggestion,
  values: ClientFormValues,
): boolean {
  return (
    normalizePostalCode(suggestion.psc ?? '') !== normalizePostalCode(values.psc ?? '') ||
    !isStateEquivalent(suggestion.state, values.state) ||
    normalizeCompareText(suggestion.city ?? '') !== normalizeCompareText(values.city ?? '') ||
    normalizeCompareText(suggestion.street ?? '') !== normalizeCompareText(values.street ?? '') ||
    normalizeCompareText(suggestion.streetNumber ?? '') !== normalizeCompareText(values.streetNumber ?? '')
  );
}

export function mergeAddressSuggestion(
  values: ClientFormValues,
  suggestion: AddressValidationSuggestion,
): ClientFormValues {
  const mergedState =
    suggestion.state && isStateEquivalent(suggestion.state, values.state)
      ? values.state
      : (suggestion.state ?? values.state);

  return {
    ...values,
    street: suggestion.street ?? values.street,
    streetNumber: suggestion.streetNumber ?? values.streetNumber,
    city: suggestion.city ?? values.city,
    psc: suggestion.psc ?? values.psc,
    state: mergedState,
  };
}

export function withAddressValidationMeta(
  values: ClientFormValues,
  status: AddressValidationPersistStatus,
): ClientFormValues {
  if (values.addressValidationStatus === status && values.addressValidatedAt) {
    return values;
  }
  return {
    ...values,
    addressValidationStatus: status,
    addressValidatedAt: new Date().toISOString(),
  };
}

export function mergeValidatedAddressIntoFormValues(
  formValues: ClientFormValues,
  validatedAddress: ClientFormValues,
): ClientFormValues {
  return {
    ...formValues,
    street: validatedAddress.street,
    streetNumber: validatedAddress.streetNumber,
    city: validatedAddress.city,
    psc: validatedAddress.psc,
    state: validatedAddress.state,
    addressValidationStatus: validatedAddress.addressValidationStatus,
    addressValidatedAt: validatedAddress.addressValidatedAt,
  };
}

export function mapFieldIssuesToFormErrors(
  response: AddressValidationResponse,
  t: TFunction,
): Partial<Record<'psc' | 'city' | 'state', string>> {
  const errors: Partial<Record<'psc' | 'city' | 'state', string>> = {};
  const issues = response.fieldIssues ?? {};

  if (issues.psc) {
    errors.psc = t('clientForm.addressValidation.fieldErrors.psc');
  }

  if (issues.city) {
    errors.city = t('clientForm.addressValidation.fieldErrors.city');
  }

  if (issues.state) {
    errors.state = t('clientForm.addressValidation.fieldErrors.state');
  }

  if (response.status === 'invalid' && Object.keys(errors).length === 0) {
    errors.psc = t('clientForm.addressValidation.fieldErrors.generic');
  }

  return errors;
}
